// src/pages/procedure/AgentSelectionPage.tsx
import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
} from "material-react-table";
import {
  Box,
  IconButton,
  Tooltip,
  Chip,
  Button,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Title from "../../../../../components/Title";
import Back from "../../../../../components/Back";
import { useLocation, useNavigate } from "react-router-dom";
import { useAgents } from "../../../../../api/agent/use-agents";
import {
  createServiceProcedure,
  ServiceProcedureCreateData,
} from "../../../../../api/catalog_procedure/use-procedure-post";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import Loader from "../../../../../components/Loader";
import FormattedDateCell from "../../../../../components/FormattedDateCell";

interface Agent {
  UUID: number;
  created_at: string;
  host: string;
  id: number;
  logic_group: string;
  meta_data?: object;
  os_type: string;
  state: boolean;
  status: string;
  subnet?: string;
  title: string;
  type: string;
  updated_at: string;
}

const AgentSelectionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { catalog } = location.state || {};
  const {
    data: agents = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useAgents();
  const { showSnackbar } = useSnackbar();

  const onlineAgents = useMemo(() => {
    return agents.filter((agent: Agent) => agent.state === true);
  }, [agents]);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const createProcedureMutation = useMutation({
    mutationFn: (newProcedureData: ServiceProcedureCreateData) =>
      createServiceProcedure(newProcedureData),
  });

  const processedAgents = useMemo(() => {
    return onlineAgents.map((agent: Agent) => ({
      ...agent,
      eligible: agent.status.toLowerCase() === "approve",
    }));
  }, [onlineAgents]);

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection]
  );

  const validSelected = useMemo(
    () =>
      processedAgents.filter(
        (agent) => selectedIds.includes(String(agent.id)) && agent.eligible
      ),
    [processedAgents, selectedIds]
  );

  const agentUUIDs = useMemo(
    () => validSelected.map((agent) => String(agent.UUID)).filter(Boolean),
    [validSelected]
  );

  const allValidSelected =
    selectedIds.length > 0 && selectedIds.length === validSelected.length;

  const handleStartProcedure = () => {
    if (!catalog || !allValidSelected) return;

    const newProcedureData: ServiceProcedureCreateData = {
      title: catalog.title,
      procedure: catalog.id,
      agents: agentUUIDs,
    };

    createProcedureMutation.mutate(newProcedureData, {
      onSuccess: (data) => {
        showSnackbar("Procedure created successfully!", "success");
        navigate(-1);
      },
      onError: (err) => {
        console.error("Error creating procedure:", err);
        showSnackbar("Error creating procedure", "error");
      },
    });
  };

  const columns = useMemo<MRT_ColumnDef<Agent>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Name",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "status",
        header: "Status",
        enableColumnFilter: true,
        filterVariant: "select",
        filterSelectOptions: ["APPROVE", "PENDING", "DECLINE"],
        sortingFn: (rowA, rowB, columnId) => {
          const statusOrder: Record<string, number> = {
            APPROVE: 1,
            PENDING: 2,
            DECLINE: 3,
          };
          const a = rowA.getValue<string>(columnId).toUpperCase();
          const b = rowB.getValue<string>(columnId).toUpperCase();
          return (statusOrder[a] || 99) - (statusOrder[b] || 99);
        },
        Cell: ({ cell }) => {
          const status = cell.getValue<string>().toUpperCase();
          let chipColor: "default" | "success" | "error" | "warning" =
            "default";
          switch (status) {
            case "APPROVE":
              chipColor = "success";
              break;
            case "PENDING":
              chipColor = "warning";
              break;
            case "DECLINE":
              chipColor = "error";
              break;
            default:
              chipColor = "default";
          }
          return <Chip label={status} color={chipColor} size="small" />;
        },
      },
      { accessorKey: "host", header: "Host", enableColumnFilter: true },
      { accessorKey: "os_type", header: "OS", enableColumnFilter: true },
      {
        accessorKey: "state",
        header: "State",
        enableColumnFilter: true,
        filterVariant: "select",
        filterSelectOptions: ["Online", "Offline"],
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue<boolean>(columnId) ? 0 : 1;
          const b = rowB.getValue<boolean>(columnId) ? 0 : 1;
          return a - b;
        },
        Cell: ({ cell }) => {
          const value = cell.getValue<boolean>();
          return (
            <Chip
              label={value ? "Online" : "Offline"}
              color={value ? "success" : "error"}
              size="small"
            />
          );
        },
      },
      { accessorKey: "subnet", header: "Subnet", enableColumnFilter: true },
      { accessorKey: "type", header: "Type", enableColumnFilter: true },
      {
        accessorKey: "created_at",
        header: "Created At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
        enableColumnFilter: true,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
        enableColumnFilter: true,
      },
    ],
    []
  );

  return (
    <Box sx={{ p: 2, position: "relative" }}>
      <Title>Select Agent for: {catalog?.title}</Title>

      {error && (
        <Typography color="error" variant="body1">
          Error loading agents
        </Typography>
      )}
      {isLoading && <Loader />}

      <MaterialReactTable
        columns={columns}
        data={processedAgents}
        getRowId={(row) => String(row.id)}
        state={{
          isLoading,
          showAlertBanner: !!error,
          showProgressBars: isRefetching,
          rowSelection,
        }}
        onRowSelectionChange={setRowSelection}
        enableRowSelection
        enableMultiRowSelection
        enableGlobalFilter
        enableSorting
        muiTableContainerProps={{
          sx: { maxHeight: "calc(80vh - 150px)" },
        }}
        initialState={{
          pagination: { pageSize: 10, pageIndex: 0 },
          density: "compact",
          showGlobalFilter: true,
        }}
        muiPaginationProps={{
          rowsPerPageOptions: [10, 25, 50, 100],
          color: "secondary",
          shape: "rounded",
        }}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              onClick={handleStartProcedure}
              disabled={
                !catalog ||
                !allValidSelected ||
                createProcedureMutation.isPending
              }
            >
              Run Procedure
            </Button>
            <Tooltip arrow title="Refresh Data">
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />

      <Back />
    </Box>
  );
};

export default AgentSelectionPage;
